import React, { useState } from 'react';
import { CrudPage } from '../../components/CrudPage';
import {
  type ProductionOrderDto,
  ProductionOrderControllerService,
} from '../../api';
import { useErrorHandler } from '../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import ProductionOrderVisualizationDialog from '../../components/ProductionOrderVisualizationDialog';
import type { CrudFormProps } from '../../components/CrudPage';

type ProductionOrderWithId = ProductionOrderDto & {
  id: string;
};

// Placeholder form: CRUD is disabled, this is never shown for add/edit
const ProductionOrderFormPlaceholder: React.FC<CrudFormProps<ProductionOrderWithId>> = () => null;

const formatDateTime = (iso?: string): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  } catch {
    return iso;
  }
};

const ProductionOrdersPage = () => {
  const { handleError } = useErrorHandler();
  const { t } = useTranslation();
  const [selectedOrderForVisualization, setSelectedOrderForVisualization] =
    useState<ProductionOrderWithId | null>(null);

  const fetchData = async (): Promise<ProductionOrderWithId[]> => {
    try {
      const orders = await ProductionOrderControllerService.findAll();
      return (orders || [])
        .filter((order): order is ProductionOrderWithId => typeof order.uuid === 'string')
        .map((order) => ({
          ...order,
          id: order.uuid || '',
        }))
        .sort((a, b) => {
          const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
          const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
          return dateB - dateA; // descending: newest first
        });
    } catch (error) {
      handleError(error, 'fetching production orders');
      return [];
    }
  };

  const saveData = async (_data: ProductionOrderWithId): Promise<void> => {
    // No-op: CRUD disabled for this debug view
  };

  const deleteData = async (_id: string): Promise<void> => {
    // No-op: CRUD disabled for this debug view
  };

  return (
    <>
      <CrudPage<ProductionOrderWithId>
        titleKey="routes.productionOrders"
        gridMeta={[
          { field: 'orderNumber', headerKey: 'productionOrder.grid.field.orderNumber', width: 140 },
          { field: 'routingName', headerKey: 'productionOrder.grid.field.routingName', width: 160 },
          { field: 'materialName', headerKey: 'productionOrder.grid.field.materialName', width: 160 },
          {
            field: 'outputQuantity',
            headerKey: 'productionOrder.grid.field.outputQuantity',
            width: 100,
            align: 'Right',
          },
          { field: 'status', headerKey: 'productionOrder.grid.field.status', width: 110 },
          {
            field: 'doughBatchCount',
            headerKey: 'productionOrder.grid.field.doughBatchCount',
            width: 100,
            align: 'Right',
          },
          {
            field: 'ovenBatchCount',
            headerKey: 'productionOrder.grid.field.ovenBatchCount',
            width: 100,
            align: 'Right',
          },
          {
            field: 'createdOn',
            headerKey: 'productionOrder.grid.field.createdOn',
            width: 150,
            type: 'date',
            render: (data) => formatDateTime(data.createdOn),
          },
          {
            field: 'lastModifiedOn',
            headerKey: 'productionOrder.grid.field.lastModifiedOn',
            width: 150,
            type: 'date',
            render: (data) => formatDateTime(data.lastModifiedOn),
          },
        ]}
        fetchData={fetchData}
        saveData={saveData}
        deleteData={deleteData}
        FormComponent={ProductionOrderFormPlaceholder}
        entityName="productionorder"
        showAddButton={false}
        showEditButton={false}
        showCloneButton={false}
        showDeleteButton={false}
        enableVirtualization
        getCustomToolbarButtons={(selectedItems) => [
          {
            id: 'visualize',
            text: t('routing.visualization.button', 'Visualisieren'),
            tooltipText: t('productionOrder.visualization.tooltip', 'Struktur des Produktionsauftrags anzeigen'),
            disabled: selectedItems.length !== 1,
            onClick: (items) => {
              if (items.length === 1) {
                setSelectedOrderForVisualization(items[0] as ProductionOrderWithId);
              }
            },
          },
        ]}
      />
      {selectedOrderForVisualization && (
        <ProductionOrderVisualizationDialog
          key={selectedOrderForVisualization.uuid ?? selectedOrderForVisualization.id}
          productionOrder={selectedOrderForVisualization}
          visible
          onClose={() => setSelectedOrderForVisualization(null)}
        />
      )}
    </>
  );
};

export default ProductionOrdersPage;
